/**
 * GiCardConnectorChips — the tiny row of connector-type chips that
 * appears on each Vault GI card. Renders nothing when the GI has
 * no linked connectors, so the card's layout is unaffected for the
 * common case.
 */

import { useTranslation } from 'react-i18next';

import type { EntityId } from '@/api/types';
import { useConnectorsForGi } from '@/hooks/useConnectors';

import { ConnectorIcon } from './ConnectorIcon';

export function GiCardConnectorChips({ root_id }: { root_id: EntityId }) {
  const { t } = useTranslation('connectors');
  const query = useConnectorsForGi(root_id);

  const connectors = query.data ?? [];
  if (connectors.length === 0) return null;

  return (
    <ul
      aria-label={t('card_chips.aria', { count: connectors.length })}
      className="mt-3 flex flex-wrap gap-1.5"
    >
      {connectors.map((c) => (
        <li
          key={c.id}
          title={c.name}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-raised px-2 py-0.5 text-[11px] text-ink-muted"
        >
          <span className="flex h-3 w-3 items-center justify-center text-spark">
            <ConnectorIcon type={c.type} className="h-3 w-3" />
          </span>
          <span className="max-w-[80px] truncate">{c.name}</span>
        </li>
      ))}
    </ul>
  );
}
